import React from "react"
import {Trans, useTranslation} from "react-i18next"
import {useNavigate} from "react-router"
import {ChevronLeftIcon} from "@heroicons/react/20/solid"
import {BriefcaseIcon, BuildingOfficeIcon, UserGroupIcon, UserPlusIcon} from "@heroicons/react/24/outline"

import {HeroLayout} from "../../components/HeroLayout.tsx"
import {Link} from "../../components/Link"
import {EGTMEvent, emitGTMEvent} from "../../utils/gtm.tsx"
import {useDocumentTitle} from "../../utils/hooks"
import {commonTransComponents} from "../../utils/i18n.tsx"

export const SignUpPick = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [showCompanyTypeSelection, setShowCompanyTypeSelection] = React.useState(false)

  useDocumentTitle(t("Signup_DocumentTitle"))

  const handleCompanyClick = () => {
    emitGTMEvent({event: EGTMEvent.COMPANY_SIGNUP_START})
    setShowCompanyTypeSelection(true)
  }

  const handleCompanyTypeSelect = (type: "managing" | "hiring") => {
    if (type === "managing") {
      navigate("/sign-up/company/managing")
    } else {
      navigate("/sign-up/company")
    }
  }

  if (showCompanyTypeSelection) {
    return (
      <HeroLayout
        leftAction={
          <button
            onClick={() => setShowCompanyTypeSelection(false)}
            className={"inline-flex items-baseline gap-1 text-cr-grey-50 hover:text-cr-blue"}
          >
            <ChevronLeftIcon className={"size-5 self-center"} />
            <span>{t("Signup_BackLink")}</span>
          </button>
        }
      >
        <div>
          <h1 className={"mb-8 text-center font-['Sora'] text-3xl font-semibold text-gray-900"}>
            {t("Signup_Pick_CompanyType_Heading")}
          </h1>
          <div className={"mb-4 flex flex-col gap-4"}>
            <button
              onClick={() => handleCompanyTypeSelect("managing")}
              className={
                "group flex w-full items-start gap-4 btn-radio-block border-2 p-6 text-left transition-all hover:border-cr-blue"
              }
            >
              <div
                className={
                  "flex size-12 items-center justify-center rounded-full bg-zinc-100 transition-colors group-hover:bg-cr-blue-light"
                }
              >
                <UserGroupIcon
                  className={"h-6 w-6 shrink-0 text-gray-500 transition-colors group-hover:text-cr-blue"}
                />
              </div>

              <div className={"flex flex-col gap-1"}>
                <div className={"text-lg font-semibold text-gray-500 transition-colors group-hover:text-cr-blue"}>
                  {t("Signup_Pick_CompanyType_Managing_Title")}
                </div>
                <div className={"text-sm text-gray-500 transition-colors group-hover:text-cr-blue"}>
                  {t("Signup_Pick_CompanyType_Managing_Subtitle")}
                </div>
              </div>
            </button>

            <button
              onClick={() => handleCompanyTypeSelect("hiring")}
              className={
                "group flex w-full items-start gap-4 btn-radio-block border-2 p-6 text-left transition-all hover:border-cr-blue"
              }
            >
              <div
                className={
                  "flex size-12 items-center justify-center rounded-full bg-zinc-100 transition-colors group-hover:bg-cr-blue-light"
                }
              >
                <UserPlusIcon className={"h-6 w-6 shrink-0 text-gray-500 transition-colors group-hover:text-cr-blue"} />
              </div>
              <div className={"flex flex-col gap-1"}>
                <div className={"text-lg font-semibold text-gray-500 transition-colors group-hover:text-cr-blue"}>
                  {t("Signup_Pick_CompanyType_Hiring_Title")}
                </div>
                <div className={"text-sm text-gray-500 transition-colors group-hover:text-cr-blue"}>
                  {t("Signup_Pick_CompanyType_Hiring_Subtitle")}
                </div>
              </div>
            </button>
          </div>
        </div>
      </HeroLayout>
    )
  }

  return (
    <HeroLayout>
      <div>
        <h1 className={"mb-8 text-center font-['Sora'] text-3xl font-semibold text-gray-900"}>
          <Trans i18nKey={"Signup_Pick_Heading"} components={commonTransComponents} />
        </h1>
        <div className={"mb-4 flex flex-col gap-4"}>
          <button
            onClick={handleCompanyClick}
            className={
              "group flex w-full items-start gap-4 btn-radio-block border-2 p-6 text-left transition-all hover:border-cr-blue"
            }
          >
            <div
              className={
                "flex size-12 items-center justify-center rounded-full bg-zinc-100 transition-colors group-hover:bg-cr-blue-light"
              }
            >
              <BuildingOfficeIcon
                className={"h-6 w-6 shrink-0 text-gray-500 transition-colors group-hover:text-cr-blue"}
              />
            </div>

            <div className={"flex flex-col gap-1"}>
              <div className={"text-lg font-semibold text-gray-500 transition-colors group-hover:text-cr-blue"}>
                {t("Signup_Pick_Company_Title")}
              </div>
              <div className={"text-sm text-gray-500 transition-colors group-hover:text-cr-blue"}>
                {t("Signup_Pick_Company_Subtitle")}
              </div>
            </div>
          </button>

          <Link
            to={"/sign-up/sales"}
            onClick={() => emitGTMEvent({event: EGTMEvent.SALES_SIGNUP_START})}
            className={
              "group flex w-full items-start gap-4 btn-radio-block border-2 p-6 text-left transition-all hover:border-cr-blue"
            }
            noUnderline
          >
            <div
              className={
                "flex size-12 items-center justify-center rounded-full bg-zinc-100 transition-colors group-hover:bg-cr-blue-light"
              }
            >
              <BriefcaseIcon className={"h-6 w-6 shrink-0 text-gray-500 transition-colors group-hover:text-cr-blue"} />
            </div>
            <div className={"flex flex-col gap-1"}>
              <div className={"text-lg font-semibold text-gray-500 transition-colors group-hover:text-cr-blue"}>
                {t("Signup_Pick_Sales_Title")}
              </div>
              <div className={"text-sm text-gray-500 transition-colors group-hover:text-cr-blue"}>
                {t("Signup_Pick_Sales_Subtitle")}
              </div>
            </div>
          </Link>
        </div>
        <div className={"text-center text-xs text-gray-500"}>
          <Trans i18nKey={"Signup_Login"} components={{loginLink: <Link to={"/log-in"} flipUnderline />}} />
        </div>
      </div>
    </HeroLayout>
  )
}
